import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconChevronDown, IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface AccountingPeriod {
  id: number;
  financial_year_id: number;
  name: string;
  start_date: string;
  end_date: string;
  status: 'open' | 'closed';
  unlocked_by: number | null;
  unlocked_at: string | null;
  unlock_reason: string | null;
}

interface FinancialYear {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  status: 'open' | 'closed';
  periods: AccountingPeriod[];
}

interface YearForm {
  name: string;
  start_date: string;
  end_date: string;
  status: 'open' | 'closed';
}

interface PeriodForm extends YearForm {
  financial_year_id: number;
  unlock_reason: string;
}

const emptyYearForm: YearForm = { name: '', start_date: '', end_date: '', status: 'open' };
const emptyPeriodForm: PeriodForm = { ...emptyYearForm, financial_year_id: 0, unlock_reason: '' };

const statusBadge = (status: string) => (
  <span className={`badge ${status === 'open' ? 'badge-active' : 'badge-neutral'}`}>
    {status === 'open' ? 'Open' : 'Closed'}
  </span>
);

export function AccountingPeriods() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [years, setYears] = useState<FinancialYear[] | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<'new' | FinancialYear | null>(null);
  const [form, setForm] = useState<YearForm>(emptyYearForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [editingPeriod, setEditingPeriod] = useState<AccountingPeriod | null>(null);
  const [periodForm, setPeriodForm] = useState<PeriodForm>(emptyPeriodForm);
  const [periodErrors, setPeriodErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<FinancialYear[]>('/financial-years').then((res) => setYears(res.data));

  useEffect(() => {
    load();
  }, []);

  const toggleExpanded = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openNew = () => {
    setForm(emptyYearForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (year: FinancialYear) => {
    setForm({ name: year.name, start_date: year.start_date, end_date: year.end_date, status: year.status });
    setErrors({});
    setEditing(year);
  };

  const set = (key: keyof YearForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/financial-years', form);
      else if (editing) await api.put(`/financial-years/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (year: FinancialYear) => {
    if (!(await confirmDelete(`Delete "${year.name}"? This removes its periods too.`))) return;
    try {
      await api.delete(`/financial-years/${year.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const generatePeriods = async (year: FinancialYear) => {
    try {
      await api.post(`/financial-years/${year.id}/generate-periods`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Could not generate periods.');
    }
  };

  const closeYear = async (year: FinancialYear) => {
    if (
      !(await confirmDelete(
        `Close "${year.name}"? Every open period under it is locked — no more GL/AP/AR postings can be dated within it until a period is individually reopened with a reason.`,
        { confirmLabel: 'Close Year' }
      ))
    )
      return;
    try {
      await api.post(`/financial-years/${year.id}/close`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Could not close the financial year.');
    }
  };

  const openEditPeriod = (period: AccountingPeriod) => {
    setPeriodForm({
      financial_year_id: period.financial_year_id,
      name: period.name,
      start_date: period.start_date,
      end_date: period.end_date,
      status: period.status,
      unlock_reason: '',
    });
    setPeriodErrors({});
    setEditingPeriod(period);
  };

  const setPeriod = (key: keyof PeriodForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setPeriodForm({ ...periodForm, [key]: e.target.value });

  // Reopening a closed period requires a reason (AccountingPeriodController
  // enforces this server-side too) — this mirrors that gate in the UI so
  // the officer sees the field before submitting, not just a 422 after.
  const reopeningClosedPeriod = editingPeriod?.status === 'closed' && periodForm.status === 'open';

  const savePeriod = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingPeriod) return;
    try {
      await api.put(`/accounting-periods/${editingPeriod.id}`, periodForm);
      setEditingPeriod(null);
      load();
    } catch (err) {
      setPeriodErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removePeriod = async (period: AccountingPeriod) => {
    if (!(await confirmDelete(`Delete period "${period.name}"?`))) return;
    try {
      await api.delete(`/accounting-periods/${period.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Accounting Periods</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add financial year
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Financial Year</th>
                <th>Start</th>
                <th>End</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {years?.flatMap((year) => {
                const isExpanded = expanded.has(year.id);
                const rows = [
                  <tr key={year.id}>
                    <td>
                      <button
                        type="button"
                        className="btn icon-btn btn-ghost"
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        onClick={() => toggleExpanded(year.id)}
                      >
                        <IconChevronDown
                          className="icon-btn-svg"
                          style={{ transform: isExpanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}
                        />
                      </button>
                    </td>
                    <td>
                      <strong>{year.name}</strong>
                    </td>
                    <td className="muted">{year.start_date}</td>
                    <td className="muted">{year.end_date}</td>
                    <td>{statusBadge(year.status)}</td>
                    <td className="right">
                      {year.periods.length === 0 && (
                        <button type="button" className="btn btn-ghost" onClick={() => generatePeriods(year)}>
                          Generate 12 monthly periods
                        </button>
                      )}
                      {year.periods.length > 0 && year.status !== 'closed' && (
                        <button type="button" className="btn btn-danger-ghost" onClick={() => closeYear(year)}>
                          Close Year
                        </button>
                      )}
                      <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(year)} />
                      <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(year)} />
                    </td>
                  </tr>,
                ];

                if (!isExpanded) return rows;

                if (year.periods.length === 0) {
                  rows.push(
                    <tr key={`${year.id}-empty`}>
                      <td></td>
                      <td colSpan={5} className="muted">
                        No periods yet.
                      </td>
                    </tr>,
                  );
                  return rows;
                }

                year.periods.forEach((period) => {
                  rows.push(
                    <tr key={`period-${period.id}`}>
                      <td></td>
                      <td>{period.name}</td>
                      <td className="muted">{period.start_date}</td>
                      <td className="muted">{period.end_date}</td>
                      <td>
                        {statusBadge(period.status)}
                        {period.unlocked_at && (
                          <div className="muted" style={{ fontSize: '0.8em' }} title={period.unlock_reason ?? ''}>
                            Unlocked {new Date(period.unlocked_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="right">
                        <IconButton icon={IconEdit} label="Edit" onClick={() => openEditPeriod(period)} />
                        <IconButton
                          icon={IconTrash}
                          label="Delete"
                          variant="danger-ghost"
                          onClick={() => removePeriod(period)}
                        />
                      </td>
                    </tr>,
                  );
                });
                return rows;
              })}
              {years?.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted center">
                    No financial years yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add financial year' : 'Edit financial year'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <input value={form.name} onChange={set('name')} required placeholder="FY2027" />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <label>
              Start Date *
              <input type="date" value={form.start_date} onChange={set('start_date')} required />
              {errors.start_date && <small className="error">{errors.start_date[0]}</small>}
            </label>
            <label>
              End Date *
              <input type="date" value={form.end_date} onChange={set('end_date')} required />
              {errors.end_date && <small className="error">{errors.end_date[0]}</small>}
            </label>
            <label className="span-2">
              Status
              <select value={form.status} onChange={set('status')}>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
              {errors.status && <small className="error">{errors.status[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {editingPeriod && (
        <Modal title="Edit period" onClose={() => setEditingPeriod(null)}>
          <form onSubmit={savePeriod} className="form-grid">
            {periodErrors.general && <div className="alert alert-error span-2">{periodErrors.general[0]}</div>}
            <label className="span-2">
              Name *
              <input value={periodForm.name} onChange={setPeriod('name')} required />
              {periodErrors.name && <small className="error">{periodErrors.name[0]}</small>}
            </label>
            <label>
              Start Date *
              <input type="date" value={periodForm.start_date} onChange={setPeriod('start_date')} required />
              {periodErrors.start_date && <small className="error">{periodErrors.start_date[0]}</small>}
            </label>
            <label>
              End Date *
              <input type="date" value={periodForm.end_date} onChange={setPeriod('end_date')} required />
              {periodErrors.end_date && <small className="error">{periodErrors.end_date[0]}</small>}
            </label>
            <label className="span-2">
              Status
              <select value={periodForm.status} onChange={setPeriod('status')}>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
              {periodErrors.status && <small className="error">{periodErrors.status[0]}</small>}
            </label>
            {reopeningClosedPeriod && (
              <label className="span-2">
                Reason for unlocking *
                <textarea
                  value={periodForm.unlock_reason}
                  onChange={setPeriod('unlock_reason')}
                  required
                  rows={2}
                  placeholder="Why is this closed period being reopened?"
                />
                {periodErrors.unlock_reason && <small className="error">{periodErrors.unlock_reason[0]}</small>}
              </label>
            )}
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditingPeriod(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
